import React from 'react';
import CreateKandi from '../../components/createKandi';
import UserKandis from '../../components/userKandis';
import FirstTimeSigninModal from '../../components/firstTimeSigninModal';
import Typography from '@mui/material/Typography';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import { Theme, createStyles } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import SplashScreen from '../../components/splashScreen';
import { HomeProps } from './types';
import { useAuth, useAppTheme, useModal, useAvatar } from '../../hooks';
import ApiService from '../../service/ApiService';
import { apiGatewayUrlParam } from '../../config/ServiceConfig';

interface MyStyles {
    pageContainer: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    pageContainer: {
        paddingTop: theme.spacing(4)
    }
  })
);

const Home: React.FunctionComponent<HomeProps> = (props: HomeProps) => {
    const classes = useStyles() as MyStyles;
    
    // Use our new state management hooks
    const { isAuthenticated, isLoading, user } = useAuth();
    const { handle, isLoaded: avatarLoaded } = useAvatar();
    const createKandiModal = useModal('createKandi');
    const firstTimeSigninModal = useModal('firstTimeSignin');
    
    // Local state for kandi refresh and onboarding
    const [lastRefreshedKandi, setLastRefreshedKandi] = React.useState(Date.now());
    const [onboardingChecked, setOnboardingChecked] = React.useState(false);

    // Extract stable references to prevent infinite loops
    const openFirstTimeSigninModal = React.useCallback(() => {
        firstTimeSigninModal.open();
    }, [firstTimeSigninModal.open]);
    
    const isFirstTimeSigninModalOpen = firstTimeSigninModal.isOpen;

    // Check onboarding status when user is authenticated
    React.useEffect(() => {
        let isMounted = true;
        
        const checkOnboardingStatus = async () => {
            if (isAuthenticated && user?.username && !onboardingChecked && !isFirstTimeSigninModalOpen) {
                try {
                    console.log('🏠 Home Debug - Checking onboarding status for:', user.username);
                    const apiService = new ApiService(apiGatewayUrlParam);
                    const onboardingStatus = await apiService.checkOnboardingStatus(user.username);
                    
                    if (!isMounted) return; // Prevent state updates if component unmounted
                    
                    console.log('🏠 Home Debug - Onboarding status:', onboardingStatus);
                    setOnboardingChecked(true);
                    
                    if (!onboardingStatus.completed) {
                        console.log('🏠 Home Debug - Opening first time signin modal - onboarding not completed');
                        openFirstTimeSigninModal();
                    }
                } catch (error) {
                    if (!isMounted) return;
                    console.error('🏠 Home Debug - Error checking onboarding status:', error);
                    setOnboardingChecked(true);
                }
            }
        };

        checkOnboardingStatus();
        
        return () => {
            isMounted = false;
        };
    }, [isAuthenticated, user?.username, onboardingChecked, isFirstTimeSigninModalOpen, openFirstTimeSigninModal]);

    const handleFirstTimeSigninClose = async () => {
        if (user?.username) {
            try {
                console.log('🏠 Home Debug - Completing onboarding for:', user.username);
                const apiService = new ApiService(apiGatewayUrlParam);
                await apiService.completeOnboarding(user.username);
                console.log('🏠 Home Debug - Onboarding completed successfully');
            } catch (error) {
                console.error('🏠 Home Debug - Error completing onboarding:', error);
            }
        }
        firstTimeSigninModal.close();
    };


    // Note: Drawer placeholder logic moved to App.tsx to avoid infinite loops

    // Render loading state
    if (isLoading) {
        return (
            <Grid 
                container 
                direction="column" 
                alignItems="center" 
                justifyContent="center" 
                style={{ minHeight: '50vh' }}
            >
                <Grid item>
                    <CircularProgress />
                </Grid>
            </Grid>
        );
    }

    // Render signed out state
    if (!isAuthenticated) {
        return <SplashScreen />;
    }

    // Render signed in state
    return (
        <div className={classes.pageContainer}>
            <Grid container spacing={0} direction="column" alignItems="center">
                <Grid item>
                    <Typography variant="body1">
                        Welcome to KandiLove{avatarLoaded ? `, ${handle}` : ''}!
                    </Typography>
                </Grid>
                <Grid item>
                    <UserKandis userName={user?.username || ''} lastUpdated={lastRefreshedKandi} />
                </Grid>
            </Grid>
            <CreateKandi 
                open={createKandiModal.isOpen} 
                onClose={createKandiModal.close} 
                setLastRefreshedKandi={setLastRefreshedKandi} 
            />
            <FirstTimeSigninModal
                open={firstTimeSigninModal.isOpen}
                onClose={handleFirstTimeSigninClose}
            />
            <Tooltip title="Add Kandi to your collection!">
                <Fab 
                    onClick={createKandiModal.open}
                    color="primary"
                >
                    <AddIcon />
                </Fab>
            </Tooltip>
            
        </div>
    );
};

export default Home;
